import React, { useState, useMemo } from 'react';

// Componentes externos
import Iconify from '../Iconify';
import Loading from '../Loading';

// Constantes y utilidades compartidas
import { PAYMENT_METHOD } from './shared/constants';

// Componentes compartidos
import { Content, BackButton } from './shared/components/styled';
import { ModalError } from './shared/components/modals/modal-error';
import { ModalSuccess } from './shared/components/modals/modal-success';
import { ModalVerificationName } from './shared/components/modals/modal-verification-name';

// Componentes Conekta
import { CardConekta } from './conekta/cards';
import { CashConekta } from './conekta/cash';
import { SpeiConekta } from './conekta/spei';

// Componentes Muwe
import { CardMuwe } from './muwe/card';
import { CashMuwe } from './muwe/cash';
import { SpeiMuwe } from './muwe/spei';

// Componentes Openpay
import { CardOpenpay } from './openpay/cards';
import { CashOpenpay } from './openpay/cash';
import { SpeiOpenpay } from './openpay/spei';

// Componentes OPM Transfer
import { SpeiOPMTransfer } from './opm-transfer';
import { useIsBanned } from './shared/hooks/use-is-banned';
import { Welcome } from './shared/components/welcome';
import { Transfer } from './shared/components/transfer';
import { FailedPayment } from './shared/components/failed-payment';

// Componentes Bitso
import { SpeiBitso } from './bitso/spei';

// Componentes Kushki
import { CardKushki } from './kushki/cards';

// Utils
import utils from '../../utils/utils';

// Componentes OrkestaPay
import { OrkestaPayCards } from './orkestapay/cards';
import { ModalIframe3DSecure } from './shared/components/modals/modal-iframe-3d-secure';
import { useGetUserPaymentMethods } from './shared/hooks/use-user-payment-methods';

// Normaliza el tipo que viene del API (ej. "other_card") al valor de PAYMENT_METHOD
const normalizePaymentType = (type) => {
  if (type === 'other_card') return PAYMENT_METHOD.OTHER_CARD;
  return type;
};

// Mapeo de componentes por método de pago y gateway
const PAYMENT_COMPONENTS = {
  [PAYMENT_METHOD.CARD]: {
    conekta: CardConekta,
    openpay: CardOpenpay,
    muwe: CardMuwe,
    kushki: CardKushki,
    orkesta: OrkestaPayCards,
  },
  [PAYMENT_METHOD.OTHER_CARD]: {
    muwe: CardMuwe,
  },
  [PAYMENT_METHOD.CASH]: {
    conekta: CashConekta,
    openpay: CashOpenpay,
    muwe: CashMuwe,
  },
  [PAYMENT_METHOD.SPEI]: {
    conekta: SpeiConekta,
    openpay: SpeiOpenpay,
    muwe: SpeiMuwe,
    opmtransfer: SpeiOPMTransfer,
    bitso: SpeiBitso,
  },
};

export const PaymentsMethods = (props) => {
  const isBanned = useIsBanned();
  const { data: userPaymentMethods, isLoading: isLoadingUserPaymentMethods } = useGetUserPaymentMethods();
  const [selectedMethod, setSelectedMethod] = useState(PAYMENT_METHOD.WELCOME);

  const PaymentComponent = useMemo(() => {
    if (isBanned) {
      return null;
    }

    if (selectedMethod === PAYMENT_METHOD.WELCOME) {
      return Welcome;
    }

    if (selectedMethod === PAYMENT_METHOD.TRANSFER) {
      return Transfer;
    }

    if (selectedMethod === PAYMENT_METHOD.FAILED_PAYMENT) {
      return FailedPayment;
    }

    // Selección desde Welcome: objeto { type, gateway, label, icon, bonus }
    if (typeof selectedMethod === 'object' && selectedMethod?.type && selectedMethod?.gateway) {
      const type = normalizePaymentType(selectedMethod.type);
      const gateway = selectedMethod.gateway;
      const Component = PAYMENT_COMPONENTS[type]?.[gateway];
      if (!Component) {
        console.warn(
          `No se encontró componente para tipo: ${type}, gateway: ${gateway}.`
        );
      }
      return Component || null;
    }

    // Compatibilidad: si seleccionan solo por constante (ej. desde otro flujo)
    if (selectedMethod === PAYMENT_METHOD.OTHER_CARD) {
      const otherCardMethod = userPaymentMethods?.find((m) => m.type === 'other_card');
      const gateway = otherCardMethod?.gateway ?? 'muwe';
      return PAYMENT_COMPONENTS[PAYMENT_METHOD.OTHER_CARD]?.[gateway] ?? CardMuwe;
    }

    return null;
  }, [userPaymentMethods, selectedMethod]);

  if (isLoadingUserPaymentMethods) {
    return (
      <Content>
        <Loading label="Cargando métodos de pago..." />
      </Content>
    );
  }

  return (
    <Content>
      <ModalError />
      <ModalSuccess />
      <ModalVerificationName />
      <ModalIframe3DSecure />

      {selectedMethod !== PAYMENT_METHOD.WELCOME && (
        <BackButton onClick={() => setSelectedMethod(PAYMENT_METHOD.WELCOME)}>
          <Iconify icon="mdi:chevron-left" />
        </BackButton>
      )}
      {PaymentComponent && (
        <PaymentComponent
          {...props}
          selectedMethod={selectedMethod}
          setSelectedMethod={setSelectedMethod}
          userPaymentMethods={userPaymentMethods}
        />
      )}
    </Content>
  );
};
